import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';

export const BookingSummary = ({
  selectedSite,
  selectedRvSite,
  stayType = 'nightly',
  checkIn,
  checkOut,
  nights,
  siteSubtotal,
  addOnsTotal,
  total,
  selectedAddOnsList,
  onSubmit,
  isProcessing,
}) => {
  const stayLabel = stayType.charAt(0).toUpperCase() + stayType.slice(1);
  const isFlatRate = stayType !== 'nightly';
  return (
    <Card
      data-testid="booking-summary-card"
      className="border border-[#B8860B]/30 shadow-xl bg-[#0B132B] sticky top-24"
    >
      <CardHeader className="bg-[#B8860B] text-[#050814] rounded-t-lg">
        <CardTitle className="text-2xl font-display">Booking Summary</CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        {selectedSite ? (
          <>
            <div>
              <h3 className="font-bold text-[#F4F1EA] text-lg mb-1">{selectedSite.name}</h3>
              {selectedSite.name === 'RV Sites' && (
                <p
                  data-testid="summary-stay-type"
                  className="text-xs uppercase tracking-widest text-[#D4AF37] mb-2"
                >
                  {stayLabel} stay
                </p>
              )}
              <p className="text-[#F4F1EA]/70 text-sm">{selectedSite.features.join(', ')}</p>
              {selectedRvSite && (
                <p
                  data-testid="summary-rv-site-label"
                  className="mt-2 text-sm text-[#B8860B] font-semibold"
                >
                  Site #{selectedRvSite.number} — {selectedRvSite.name}
                </p>
              )}
            </div>

            <div className="space-y-3 pt-4 border-t border-[#B8860B]/20">
              {checkIn && (
                <div className="flex justify-between text-sm">
                  <span className="text-[#F4F1EA]/70">Check-in:</span>
                  <span className="font-semibold text-[#F4F1EA]">{checkIn.toLocaleDateString()}</span>
                </div>
              )}
              {checkOut && (
                <div className="flex justify-between text-sm">
                  <span className="text-[#F4F1EA]/70">Check-out:</span>
                  <span className="font-semibold text-[#F4F1EA]">{checkOut.toLocaleDateString()}</span>
                </div>
              )}
              {checkIn && checkOut && (
                <div className="flex justify-between text-sm">
                  <span className="text-[#F4F1EA]/70">Nights:</span>
                  <span className="font-semibold text-[#F4F1EA]">{nights}</span>
                </div>
              )}
            </div>

            {checkIn && checkOut && (
              <>
                <div className="space-y-2 pt-4 border-t border-[#B8860B]/20">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#F4F1EA]/70">
                      {isFlatRate
                        ? `${stayLabel} flat rate`
                        : `$${selectedSite.price.toFixed(2)} × ${nights} nights`}
                    </span>
                    <span className="font-semibold text-[#F4F1EA]">${siteSubtotal.toFixed(2)}</span>
                  </div>

                  {selectedAddOnsList && selectedAddOnsList.length > 0 && (
                    <div className="pt-2 border-t border-[#B8860B]/10 space-y-1">
                      <p className="text-xs uppercase tracking-wider text-[#B8860B]/80 mb-1">Add-ons</p>
                      {selectedAddOnsList.map((item) => (
                        <div key={item.id} className="flex justify-between text-sm">
                          <span className="text-[#F4F1EA]/70">
                            {item.name} × {item.quantity}
                          </span>
                          <span className="font-semibold text-[#F4F1EA]">
                            ${(item.price * item.quantity).toFixed(2)}
                          </span>
                        </div>
                      ))}
                      <div className="flex justify-between text-sm pt-1">
                        <span className="text-[#F4F1EA]/70">Add-ons subtotal</span>
                        <span className="font-semibold text-[#B8860B]">${addOnsTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t-2 border-[#B8860B]">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-[#F4F1EA]">Total today:</span>
                    <span className="text-2xl font-bold text-[#B8860B]" data-testid="booking-total">
                      ${total.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-[10px] text-[#F4F1EA]/55 mt-2 leading-relaxed">
                    + metered electricity, billed at check-out based on usage.
                  </p>
                </div>

                <Button
                  data-testid="proceed-to-payment-btn"
                  onClick={onSubmit}
                  disabled={isProcessing}
                  className="w-full bg-[#B8860B] hover:bg-[#D4AF37] text-[#050814] font-semibold py-6 text-lg rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? 'Processing…' : 'Proceed to Payment'}
                </Button>
              </>
            )}
          </>
        ) : (
          <div className="text-center py-8">
            <p className="text-[#F4F1EA]/60">Select a site type to see pricing</p>
          </div>
        )}

        <div className="pt-6 border-t border-[#B8860B]/20 text-xs text-[#F4F1EA]/60 space-y-2">
          <p>• Secure payment processing</p>
          <p>• Instant booking confirmation</p>
          <p>• Free cancellation up to 48 hours</p>
          <p>• Adults Only (21+) Resort</p>
        </div>
      </CardContent>
    </Card>
  );
};
